/**
 * Per-type UI icons for sketch objects — full coverage with zero mandatory
 * artwork.
 *
 * Resolution order for a type id:
 *  1. The type definition's explicit `icon` (an `*.svg?react` component set
 *     in its `defs/` file), when one is declared.
 *  2. An icon AUTO-GENERATED from the type's own 2D glyph: the default-param
 *     glyph segments are fitted (padded, Y-flipped — world +Y is up, SVG +Y
 *     is down) into a 24×24 viewBox and stroked with `currentColor`, the
 *     same treatment the tool-settings library thumbnails use on canvas.
 *  3. The generic cube, only for unknown/unset type ids.
 *
 * Components are cached per type id so rows get a stable component identity
 * across renders.
 */

import type { SVGProps } from 'react';
import CubeIcon from '../../assets/icons/cube.svg?react';
import { getSketchObjectType, onSketchObjectTypesChanged, type RegisteredSketchObjectType } from './registry';
import type { SketchObjectFrame, SketchObjectTypeIcon } from './types';
// Importing the catalog registers every type — the left panel resolves icons
// without depending on the sketch-object tool having been opened first.
import './defs';

const VIEWBOX = 24;
/** Matches the built-in padding of the hand-drawn toolbar icons. */
const PADDING = 3;

/**
 * SVG path ("M x y L x y …") of flat `[x1,y1,z, x2,y2,z, …]` segments fitted
 * (padded, Y-flipped) into a `size`×`size` viewBox. Null when empty.
 */
export function glyphSegmentsPath(segments: readonly number[], size: number = VIEWBOX, padding: number = PADDING): string | null {
  if (segments.length < 6) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < segments.length; i += 3) {
    minX = Math.min(minX, segments[i]);
    maxX = Math.max(maxX, segments[i]);
    minY = Math.min(minY, segments[i + 1]);
    maxY = Math.max(maxY, segments[i + 1]);
  }
  if (!isFinite(minX)) return null;

  const scale = Math.min((size - 2 * padding) / Math.max(maxX - minX, 1e-6), (size - 2 * padding) / Math.max(maxY - minY, 1e-6));
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const px = (x: number): string => (size / 2 + (x - cx) * scale).toFixed(2);
  const py = (y: number): string => (size / 2 - (y - cy) * scale).toFixed(2);

  const parts: string[] = [];
  for (let i = 0; i + 5 < segments.length; i += 6) {
    parts.push(`M ${px(segments[i])} ${py(segments[i + 1])} L ${px(segments[i + 3])} ${py(segments[i + 4])}`);
  }
  return parts.join(' ');
}

/** Default-param glyph of a type at its default size, centered on the origin. */
export function defaultTypeGlyph(type: RegisteredSketchObjectType): number[] {
  const frame: SketchObjectFrame = {
    center: { x: 0, y: 0 },
    dir: { x: 1, y: 0 },
    norm: { x: 0, y: 1 },
    width: type.defaultSize.width,
    depth: type.defaultSize.depth,
    z: 0,
  };
  try {
    return type.buildDefaultGlyph(frame);
  } catch {
    return [];
  }
}

const iconCache = new Map<string, SketchObjectTypeIcon>();

// Organisation (custom) types are re-registered when republished: their
// generated icons must follow. Built-in icons never change.
onSketchObjectTypesChanged(() => {
  for (const id of iconCache.keys()) {
    const type = getSketchObjectType(id);
    if (!type || type.custom) iconCache.delete(id);
  }
});

/**
 * Icon component for a sketch-object type id. Always returns a usable
 * component (see the resolution order in the module doc).
 */
export function getSketchObjectTypeIcon(typeId: string): SketchObjectTypeIcon {
  const cached = iconCache.get(typeId);
  if (cached) return cached;

  const type = getSketchObjectType(typeId);
  if (!type) return CubeIcon; // unknown id — don't cache, the registry may still grow

  let icon: SketchObjectTypeIcon;
  if (type.icon) {
    icon = type.icon;
  } else {
    const d = glyphSegmentsPath(defaultTypeGlyph(type));
    if (d) {
      // The path data is precomputed and the component identity is cached, so
      // the render itself is a single static <path> — no memo needed.
      const GlyphIcon: SketchObjectTypeIcon = (props: SVGProps<SVGSVGElement>) => (
        <svg viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} width="24" height="24" xmlns="http://www.w3.org/2000/svg" {...props}>
          <path d={d} fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" />
        </svg>
      );
      GlyphIcon.displayName = `SketchObjectGlyphIcon(${typeId})`;
      icon = GlyphIcon;
    } else {
      icon = CubeIcon;
    }
  }
  iconCache.set(typeId, icon);
  return icon;
}
